import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  StatusIndicator,
  PropertyFilterProps,
  Toggle,
  TextFilter,
  Spinner,
  Pagination,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useSplitPanel } from '../../context/SplitPanelContext';
import { useUsers } from '../../store/users/useUsers';
import { User } from 'src/types/orbit/User';
import UserDetailsForm from 'src/components/user/UserDetailsForm';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import FilterModal from 'src/components/filter/FilterModel';
import { ExportButton } from 'src/components/ExportButton';
import useUserProfile from 'src/hooks/useUserProfile';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import { Persona } from 'src/contstant/Persona';
import {
  defaultVisibleColumns,
  getExportUserConfig,
  getFilterUsersFilterProperties,
  getItemValue,
  getSearchableFields,
  getUsersTableDefiniation,
  initialFilters,
  UserPropertyKey,
} from './usersConfig';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useRoles } from 'src/store/role/useRoles';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import TablePreferences, { ColumnDefinition } from 'src/components/TablePreferences';
import { PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';

// Main users management page component
const UsersPage = () => {
  // Split panel controls for user details view
  const { setContent, setTitle, closeView, setPanelSize } = useSplitPanel();
  const [selectedItems, setSelectedItems] = useState<User[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();

  const { getPersona, getUserProgramId, getUserId, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      tokens: [],
      operation: 'and' as const,
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'users'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);
  const {
    loadUsers,
    fetchUsersApiStatus,
    updateUserApiStatus,
    deleteUserApiStatus,
    checkUserPermissions,
    clearAPIStatusMessage,
    usersList,
    userPermission,
    currentUser,
    resetApiStatus,
    clearNextToken,
  } = useUsers();

  // Column configuration for table preferences
  const columnPreferences: ColumnDefinition[] = getUsersTableDefiniation().map((col) => ({
    id: col.id,
    label: col.header as string,
    editable: true,
  }));

  // Table display and pagination state
  const [tablePrefs, setTablePrefs] = useState({
    visibleColumns: getUsersTableDefiniation(businessUnitList).filter((col) =>
      defaultVisibleColumns.includes(col.id),
    ),
    paginatedItems: [],
    currentPage: 1,
    pageSize: PaginationConfig.USERS_MAX_RESULT,
  });

  // Handler for table preferences changes
  const handlePreferencesChange = (data: {
    visibleColumns: any[];
    paginatedItems: any[];
    currentPage: number;
    pageSize: number;
  }) => {
    // Use type assertion to match the expected state type
    setTablePrefs((prev) => ({
      ...prev,
      visibleColumns: data.visibleColumns,
      currentPage: data.currentPage,
      pageSize: data.pageSize as PaginationConfig,
      // Keep the paginatedItems type as is from the previous state
      // but update with new values
      paginatedItems: data.paginatedItems as typeof prev.paginatedItems,
    }));
  };

  // User profile and role management hooks
  const { loadRoles, roles } = useRoles();

  // Generic filtering and search functionality
  const {
    searchText,
    setSearchText: setSearchTextInternal,
    query,
    setQuery,
    filteredItems,
    filterOptions,
    setFilterOptions,
  } = useGenericFilter<User>(
    usersList,
    getItemValue,
    getSearchableFields,
    initialFilters,
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Temporary query state for filter modal (only applied on "Apply" click)
  const [tempQuery, setTempQuery] = useState(query);

  // Use custom hook for PropertyFilter persistence (search text + query tokens sync)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
    setQuery,
  );

  // Local pagination for filtered results
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: tablePrefs.pageSize,
    });

  // Check if current user has edit permissions
  const isEditUser = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.USERS.EDIT_USER,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  // Track if we've already loaded initial data
  const hasLoadedInitialData = useRef(false);

  // Initialize data and permissions on component mount
  useEffect(() => {
    // Only run once on mount
    if (hasLoadedInitialData.current) {
      return;
    }

    hasLoadedInitialData.current = true;

    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [permissionConfig.USERS.EDIT_USER],
        divisions: getDivisions(),
      });
    }
    setCurrentPageIndex(1);
    loadData();
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
    }

    loadRoles({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId(),
      isRoleActive: true,
      persona: getPersona(),
    });

    return () => {
      closeView();
    };
  }, []);

  // Track if we're currently auto-paginating to prevent duplicate calls
  const isAutoPaginatingRef = useRef(false);

  // Auto-fetch next page when nextToken is available
  useEffect(() => {
    if (
      fetchUsersApiStatus.nextToken &&
      !fetchUsersApiStatus.isLoading &&
      fetchUsersApiStatus.isSuccess !== false &&
      !isAutoPaginatingRef.current
    ) {
      isAutoPaginatingRef.current = true;
      loadUsers({
        programId: getUserProgramId(),
        nextToken: fetchUsersApiStatus.nextToken,
        isDeleted: false,
        businessUnit: getUserBusinessUnitId(),
      });
    }

    // Reset the flag when loading completes
    if (!fetchUsersApiStatus.isLoading) {
      isAutoPaginatingRef.current = false;
    }
  }, [fetchUsersApiStatus.nextToken, fetchUsersApiStatus.isLoading]);

  // Generate dynamic filter options from business units and roles
  useEffect(() => {
    setFilterOptions(initialFilters);
    const newRolesFilterValues: any[] = [];

    if (roles.length > 0) {
      roles.forEach((bu) => {
        newRolesFilterValues.push({
          propertyKey: UserPropertyKey.ROLE,
          value: bu.roleId,
          label: bu.name,
        });
      });
      setFilterOptions((prev) => {
        return [...prev, ...newRolesFilterValues];
      });
    }
  }, [businessUnitList, roles]);

  const loadData = async (nextToken?: string, maxResults?: number) => {
    // Clear nextToken before fresh load to ensure we start from beginning
    if (!nextToken) {
      clearNextToken();
    }
    // Initial call to load first page
    await loadUsers({
      programId: getUserProgramId(),
      maxResults: maxResults,
      nextToken: nextToken,
      isDeleted: false,
      businessUnit: getUserBusinessUnitId(),
    });
  };
  // Handle table row selection and open details panel
  const handleSelectionChange = ({ detail }: { detail: { selectedItems: User[] } }) => {
    setSelectedItems(detail.selectedItems);
    openUserDetailsView('Update User', detail.selectedItems[0]);
  };

  // Open user details in split panel
  const openUserDetailsView = (title: string, user?: User | null) => {
    resetApiStatus();
    setPanelSize(window.innerWidth * 0.3);
    setTitle(isEditUser() ? title : 'User Details');
    setContent(
      <UserDetailsForm
        user={user}
        onViewdissmiss={() => {
          setSelectedItems([]);
        }}
      />,
    );
  };

  // Table header with action buttons
  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => {
              setCurrentPageIndex(1);
              hasLoadedInitialData.current = false;
              loadData();
            }}
          />
          <ExportButton
            data={filteredItems || []}
            config={getExportUserConfig(businessUnitList)}
            onError={(error) => {
              alert(error);
            }}
          />

          {isEditUser() && (
            <Button
              variant="normal"
              data-testid="add-user-button"
              onClick={() => openUserDetailsView('Create User', null)}
            >
              Create User
            </Button>
          )}
        </SpaceBetween>
      }
    >
      Users
    </Header>
  );

  return (
    <ContentLayout>
      <ApiStatusFlashBar
        apiStatus={[fetchUsersApiStatus, updateUserApiStatus, deleteUserApiStatus]}
        clearMessages={() => {
          clearAPIStatusMessage();
        }}
      />

      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          setTempQuery(query); // Reset temp query to current query
          setShowFilterModal(false);
        }}
        headerText="Filter users"
        query={tempQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          setTempQuery(detail as any);
        }}
        loading={false}
        filteringProperties={getFilterUsersFilterProperties(
          getPersona() === Persona.PLATFORM_ADMIN,
        )}
        filteringOptions={filterOptions}
        filteringPlaceholder="Filter users"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          setCurrentPageIndex(1);
          setQuery(tempQuery); // Apply temp query to actual query
          setQueryFilters({ ...tempQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />

      <Box margin={{ top: 's' }}>
        <Table
          data-testid="users-table"
          columnDefinitions={
            tablePrefs.visibleColumns.length > 0
              ? tablePrefs.visibleColumns
              : getUsersTableDefiniation(businessUnitList)
          }
          items={paginatedItems}
          selectedItems={selectedItems}
          selectionType="single"
          loading={fetchUsersApiStatus.isLoading}
          loadingText="Loading Users..."
          header={tableHeader}
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchText}
                filteringPlaceholder="Search Users"
                filteringAriaLabel="Search Users"
                onChange={({ detail }) => {
                  setSearchText(detail.filteringText);
                }}
              />
              <Button
                data-testid="filter-button"
                iconName="filter"
                onClick={() => {
                  setTempQuery(query); // Initialize temp query with current query
                  setShowFilterModal(true);
                }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
              </Button>
            </SpaceBetween>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          preferences={
            <TablePreferences
              tableId="users-table"
              defaultVisibleColumns={defaultVisibleColumns}
              defaultPageSize={PaginationConfig.USERS_MAX_RESULT}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              allColumnDefinitions={getUsersTableDefiniation(businessUnitList)}
              allItems={filteredItems || []}
              onPreferencesChange={handlePreferencesChange}
            />
          }
          onSelectionChange={handleSelectionChange}
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Users.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
};

export default UsersPage;
